/**
 * Shared API error handling utilities
 *
 * This module provides consistent error handling across all API calls in the application.
 * Use these utilities instead of implementing custom error handling in each API hook.
 */

/**
 * Structured API error data from backend
 */
export interface ApiErrorData {
  code?: number;
  message: string;
  error_type?: string;
  file_path?: string;
  suggestion?: string;
  retryable?: boolean;
  detail?: string;
}

/**
 * Custom error class that preserves structured error information from backend
 *
 * @example
 * try {
 *   await fetchWithAuth('/api/endpoint');
 * } catch (error) {
 *   if (error instanceof ApiError) {
 *     console.log(error.code);       // Backend error code
 *     console.log(error.suggestion); // Helpful suggestion for the user
 *     console.log(error.filePath);   // Related file path (for file operations)
 *     console.log(error.isRetryable); // Whether operation can be retried
 *   }
 * }
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly data: ApiErrorData;

  constructor(status: number, data: ApiErrorData) {
    super(data.message || data.detail || 'Unknown error');
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  get code(): number | undefined {
    return this.data.code;
  }

  get errorType(): string | undefined {
    return this.data.error_type;
  }

  get filePath(): string | undefined {
    return this.data.file_path;
  }

  get suggestion(): string | undefined {
    return this.data.suggestion;
  }

  get isRetryable(): boolean {
    return this.data.retryable ?? true;
  }
}

/**
 * Parse error response from API, attempting JSON first, falling back to text
 *
 * @param response - The Response object from fetch
 * @returns Parsed error data
 */
export async function parseErrorResponse(response: Response): Promise<ApiErrorData> {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    try {
      const json = await response.json();
      return {
        code: json.code,
        message: json.message || json.detail || JSON.stringify(json),
        error_type: json.error_type,
        file_path: json.file_path,
        suggestion: json.suggestion,
        retryable: json.retryable,
        detail: json.detail
      };
    } catch {
      // JSON parse failed, fall through to text
    }
  }

  const text = await response.text();
  // Try to parse as JSON in case content-type header is wrong
  try {
    const json = JSON.parse(text);
    return {
      code: json.code,
      message: json.message || json.detail || text,
      error_type: json.error_type,
      file_path: json.file_path,
      suggestion: json.suggestion,
      retryable: json.retryable,
      detail: json.detail
    };
  } catch {
    // Not JSON, return as plain text message
    return { message: text || `HTTP ${response.status} error` };
  }
}

/**
 * Throw ApiError if response is not ok
 *
 * @param response - The Response object from fetch
 * @param context - Context string for error message (e.g., "Initialize session")
 * @throws {ApiError} If response.ok is false
 *
 * @example
 * const response = await fetch('/api/endpoint');
 * await throwIfNotOk(response, 'Fetch data');
 * const data = await response.json();
 */
export async function throwIfNotOk(response: Response, context: string): Promise<void> {
  if (!response.ok) {
    const errorData = await parseErrorResponse(response);
    throw new ApiError(response.status, {
      ...errorData,
      message: errorData.message || `${context} failed`
    });
  }
}

/**
 * Type guard to check if an error is an ApiError
 *
 * @param error - The error to check
 * @returns True if error is an ApiError
 *
 * @example
 * try {
 *   await apiCall();
 * } catch (error) {
 *   if (isApiError(error)) {
 *     // TypeScript knows error is ApiError here
 *     console.log(error.code);
 *   }
 * }
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extract user-friendly error message from any error
 *
 * @param error - Any error object
 * @param fallback - Fallback message if error message is empty
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, fallback: string = 'An error occurred'): string {
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message || fallback;
  }
  if (typeof error === 'string') {
    return error;
  }
  return fallback;
}
